import {
  PreScreeningQuestion,
  QuestionType,
  QuestionOption,
} from "../../../types/landingPage";
import { AttributeValueInput } from "../../../utils/attributeMapping/types";

interface MedicationOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface AttributeThreshold {
  attributeDefinitionId: number;
  minCount: number;
}

interface MedicationChecklistConfig {
  id: string;
  question: string;
  helpText?: string;
  disqualifyMessage: string;
  allowBack?: boolean;
  /** Minimum number of medication selections required to qualify */
  minCount: number;
  /** Medication options to display (brand names with generics) */
  medications: MedicationOption[];
  /** The exclusive "I don't take any" option */
  noneOption: MedicationOption;
  /** Optional "another medication not listed" catch-all */
  otherOption?: MedicationOption;
  /** Maps selection counts to boolean attributes */
  attributeThresholds: AttributeThreshold[];
}

/**
 * Creates a CHECKBOX-based medication recognition question.
 *
 * Instead of asking users to count their medications, this shows
 * actual medication names for users to select. The system counts
 * selections to determine qualification.
 *
 * @example
 * createMedicationChecklist({
 *   id: "diabetes_medications_count",
 *   question: "Which of these diabetes medications are you currently taking?",
 *   helpText: "Select all that apply.",
 *   disqualifyMessage: "This study requires at least 2 diabetes medications.",
 *   minCount: 2,
 *   medications: [
 *     { value: "metformin", label: "Metformin (Glucophage)" },
 *     { value: "semaglutide", label: "Ozempic, Wegovy, or Rybelsus (semaglutide)" },
 *   ],
 *   noneOption: { value: "none_diabetes_meds", label: "I don't take any diabetes medications" },
 *   attributeThresholds: [
 *     { attributeDefinitionId: 26112, minCount: 2 },
 *   ],
 * })
 */
export function createMedicationChecklist(
  config: MedicationChecklistConfig,
): PreScreeningQuestion {
  const {
    id,
    question,
    helpText,
    disqualifyMessage,
    allowBack = true,
    minCount,
    medications,
    noneOption,
    otherOption,
    attributeThresholds,
  } = config;

  const countCheck = (
    _allAnswers: Record<
      string,
      string | string[] | number | null | undefined
    >,
    currentAnswer?: string | string[],
  ): boolean => {
    const selections = Array.isArray(currentAnswer) ? currentAnswer : [];
    const medCount = selections.filter((s) => s !== noneOption.value).length;
    return medCount < minCount;
  };

  const medicationOptions: QuestionOption[] = medications.map((med) => ({
    value: med.value,
    label: med.label,
    ...(med.sublabel && { sublabel: med.sublabel }),
    disqualify: countCheck,
  }));

  if (otherOption) {
    medicationOptions.push({
      value: otherOption.value,
      label: otherOption.label,
      disqualify: countCheck,
    });
  }

  medicationOptions.push({
    value: noneOption.value,
    label: noneOption.label,
    exclusive: true,
    disqualify: true,
  });

  return {
    id,
    type: QuestionType.CHECKBOX,
    question,
    helpText,
    required: true,
    allowBack,
    options: medicationOptions,
    disqualifyMessage,
    attributeMapping: (response: unknown): AttributeValueInput[] => {
      const selections = Array.isArray(response) ? response : [];
      const medCount = selections.filter(
        (s: string) => s !== noneOption.value,
      ).length;

      return attributeThresholds.map((threshold) => ({
        attributeDefinitionId: threshold.attributeDefinitionId,
        booleanAttributeValue: medCount >= threshold.minCount,
        isSkipped: false,
      }));
    },
  };
}
